// Google Analytics utility functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Check if Google Analytics should be enabled
const isGAEnabled = () => {
  // Check for REACT_APP_ENVIRONMENT variable first, fallback to NODE_ENV
  const environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV;
  
  // Only enable GA in production
  return environment === 'production' || environment === 'prod';
};

// Initialize gtag - this should be called once when the app loads
export const initializeGA = (measurementId: string) => {
  // Only initialize in production
  if (!isGAEnabled()) {
    console.log(`GA: Skipping initialization in ${process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV} environment`);
    
    // Create dummy gtag function to prevent errors
    window.gtag = function() {
      // No-op in non-production environments
    };
    return;
  }

  // Check if already initialized
  if (typeof window.gtag !== 'undefined') {
    return; // Already initialized
  }

  // Add the Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId);
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (!isGAEnabled() || typeof window.gtag !== 'function') return;
  
  window.gtag('config', 'G-G13V47F3PZ', {
    page_path: path,
    page_title: title,
  });
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!isGAEnabled() || typeof window.gtag !== 'function') return;
  
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// E-commerce specific events
export const trackEcommerce = {
  // Track when items are viewed
  viewItem: (item: {
    id: string;
    name: string;
    category: string;
    price: number;
  }) => {
    if (!isGAEnabled() || typeof window.gtag !== 'function') return;
    
    window.gtag('event', 'view_item', {
      currency: 'USD',
      value: item.price,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: 1
      }]
    });
  },

  // Track when items are added to cart
  addToCart: (item: {
    id: string;
    name: string;
    category: string;
    price: number;
    quantity: number;
  }) => {
    if (!isGAEnabled() || typeof window.gtag !== 'function') return;
    
    window.gtag('event', 'add_to_cart', {
      currency: 'USD',
      value: item.price * item.quantity,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: item.quantity
      }]
    });
  },

  // Track when checkout begins
  beginCheckout: (items: any[], total: number) => {
    if (!isGAEnabled() || typeof window.gtag !== 'function') return;
    
    window.gtag('event', 'begin_checkout', {
      currency: 'USD',
      value: total,
      items: items.map(item => ({
        item_id: item.product.id,
        item_name: item.product.name,
        item_category: item.product.category,
        price: item.price,
        quantity: item.quantity
      }))
    });
  },

  // Track successful purchases
  purchase: (orderId: string, items: any[], total: number, tax: number) => {
    if (!isGAEnabled() || typeof window.gtag !== 'function') return;
    
    window.gtag('event', 'purchase', {
      transaction_id: orderId,
      currency: 'USD',
      value: total,
      tax: tax,
      items: items.map(item => ({
        item_id: item.product.id,
        item_name: item.product.name,
        item_category: item.product.category,
        price: item.price,
        quantity: item.quantity
      }))
    });
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string, formData?: any) => {
  trackEvent('form_submit', 'engagement', formName);
};

// Track search queries
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  if (!isGAEnabled() || typeof window.gtag !== 'function') return;
  
  window.gtag('event', 'search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

// Track user engagement
export const trackEngagement = {
  // Track file downloads
  downloadFile: (fileName: string) => {
    trackEvent('file_download', 'engagement', fileName);
  },

  // Track external links
  clickExternalLink: (url: string) => {
    trackEvent('click', 'external_link', url);
  },

  // Track social shares
  share: (platform: string, contentId?: string) => {
    trackEvent('share', 'social', platform, undefined);
  },

  // Track scroll depth
  scrollDepth: (percentage: number) => {
    trackEvent('scroll', 'engagement', `${percentage}%`, percentage);
  }
};

// Track errors
export const trackError = (errorMessage: string, errorStack?: string) => {
  if (!isGAEnabled() || typeof window.gtag !== 'function') return;
  
  window.gtag('event', 'exception', {
    description: errorMessage,
    fatal: false
  });
};